import { StoryFormData, StoryResponse } from '../types/story';
import { supabase } from './supabaseClient';
import { logger } from '../utils/logger';
import { ApiService } from './api';

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

// Enhanced debug info
logger.log('=== API CONNECTION DEBUG INFO ===');
logger.log('API_BASE_URL:', API_BASE_URL);
logger.log('REACT_APP_API_URL environment variable:', process.env.REACT_APP_API_URL);
logger.log('Current origin:', window.location.origin);
logger.log('================================');

// Suggestions response interface
export interface SuggestionsResponse {
  suggestions: string[];
}

// Log all API requests
const logApiRequest = (method: string, url: string) => {
  logger.log(`[API REQUEST] ${method} ${url} at ${new Date().toISOString()}`);
};

// Helper function to handle response errors
const handleResponseError = async (response: Response): Promise<string> => {
  let errorMessage = `Request failed: ${response.status}`;
  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorMessage;
  } catch (e) {
    // If we can't parse the response as JSON, use the default error message
  }
  return errorMessage;
};

// Create a helper function to transform database records to API format
const transformDatabaseStory = (story: any): StoryResponse => {
  return {
    id: story.id,
    title: story.title,
    text: story.text,
    audioUrl: story.audio_url,
    setting: story.setting,
    characters: story.characters,
    mood: story.mood,
    voice: story.voice,
    length: story.length,
    backgroundNoise: story.background_noise,
    additionalDetails: story.additional_details,
    isPublic: story.is_public,
    isFavorite: story.is_favorite,
    is_favorite: story.is_favorite, // Include both forms for compatibility
    shareCode: story.share_code,
    createdAt: story.created_at
  };
};

// Helper function to process audio URLs and normalize data
const processAudioUrl = (story: StoryResponse): StoryResponse => {
  // Process audio URL
  if (story.audioUrl && story.audioUrl.startsWith('http')) {
    // Already an absolute URL (from Supabase storage), leave it as is
    logger.log('Using absolute audio URL from storage:', story.audioUrl);
  }
  
  // Normalize favorite status - handle both snake_case and camelCase
  if (story.is_favorite !== undefined && story.isFavorite === undefined) {
    story.isFavorite = story.is_favorite;
  } else if (story.isFavorite !== undefined && story.is_favorite === undefined) {
    story.is_favorite = story.isFavorite;
  }
  
  return story;
};

// Helper to log response headers in a TypeScript-friendly way
const logResponseHeaders = (response: Response) => {
  const headerObj: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    headerObj[key] = value;
  });
  logger.log('Response headers:', headerObj);
};

// Retry function with exponential backoff
const fetchWithRetry = async (url: string, options: RequestInit, maxRetries = 3): Promise<Response> => {
  let retries = 0;
  
  logApiRequest(options.method || 'GET', url);
  logger.log('Full URL:', url);
  logger.log('Request options:', JSON.stringify(options, null, 2));
  
  while (retries < maxRetries) {
    try {
      logger.log(`Attempt ${retries + 1} to fetch ${url}`);
      
      // Enhanced options with CORS settings
      const enhancedOptions: RequestInit = {
        ...options,
        mode: 'cors',
        credentials: 'omit',
        headers: {
          ...options.headers,
          'Accept': 'application/json',
        }
      };
      
      logger.log('Enhanced options:', JSON.stringify(enhancedOptions, null, 2));
      
      const response = await fetch(url, enhancedOptions);
      
      logger.log(`Response status: ${response.status}`);
      logResponseHeaders(response);
      
      if (response.ok) {
        logger.log('Response was OK, returning');
        return response;
      }
      
      // If it's not a CORS error or another network error, don't retry
      if (response.status !== 0 && response.status !== 503 && response.status !== 504) {
        logger.warn(`Non-retryable error: ${response.status}`);
        return response;
      }
    } catch (err) {
      logger.error(`Attempt ${retries + 1} failed with error:`, err);
    }
    
    // Exponential backoff delay: 500ms, 1000ms, 2000ms, etc.
    const delay = Math.pow(2, retries) * 500;
    logger.log(`Waiting ${delay}ms before retrying...`);
    await new Promise(resolve => setTimeout(resolve, delay));
    retries++;
  }
  
  // If all retries failed, make one last attempt and return whatever happens
  logger.log(`Final attempt to fetch ${url}`);
  return fetch(url, options);
};

// Helper to add auth token to request headers
const addAuthHeader = (headers: Record<string, string>, token: string | null): Record<string, string> => {
  if (token) {
    return {
      ...headers,
      'Authorization': `Bearer ${token}`
    };
  }
  return headers;
};

// Track API calls for debugging
const serviceCallCounts = {
  getUserStories: 0,
  getFavoriteStories: 0
};

// Story service with API calls
export const StoryService = {
  // Generate a new story
  generateStory: async (formData: StoryFormData, token: string | null = null): Promise<StoryResponse> => {
    try {
      const headers = addAuthHeader({
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }, token);

      logger.log('Sending story generation request to Edge Function...');
      
      // Add better logging
      logger.log('Story request payload:', JSON.stringify(formData, null, 2));

      const response = await fetchWithRetry(`${API_BASE_URL}/stories`, {
        method: 'POST',
        headers,
        body: JSON.stringify(formData),
      }, 3);
      
      // More detailed response logging
      logger.log('Edge Function response status:', response.status);
      
      // Use the helper function instead of inline iterator conversion
      logResponseHeaders(response);
      
      if (!response.ok) {
        let errorMessage = `Failed to generate story: ${response.status}`;
        try {
          const errorData = await response.json();
          logger.error('Error response body:', errorData);
          errorMessage = errorData.error?.message || errorData.message || errorMessage;
        } catch (e) {
          logger.error('Failed to parse error response:', e);
        }
        throw new Error(errorMessage);
      }
      
      // Parse the response more carefully
      const rawData = await response.text();
      logger.log('Raw response text:', rawData);
      
      let data;
      try {
        data = JSON.parse(rawData);
      } catch (e) {
        logger.error('Failed to parse JSON response:', e);
        throw new Error('Invalid response from server: could not parse JSON');
      }
      
      // Extract the actual story data from the response
      // The Edge Function returns { data: { id, status, message }, error: null }
      const storyData = data.data || data;
      
      logger.log('Parsed story response:', storyData);
      
      if (!storyData || !storyData.id) {
        logger.error('No ID in response:', storyData);
        throw new Error('Story was generated but no ID was returned');
      }
      
      // Fetch the complete story data using the ID
      // This ensures we have all the needed fields for the StoryResponse
      logger.log('Fetching complete story data with ID:', storyData.id);
      const completeStory = await ApiService.getStory(storyData.id);
      
      // If the audioUrl is a relative path, convert it to an absolute URL
      if (completeStory.audioUrl && completeStory.audioUrl.startsWith('/')) {
        const baseUrl = API_BASE_URL.substring(0, API_BASE_URL.lastIndexOf('/api'));
        completeStory.audioUrl = `${baseUrl}${completeStory.audioUrl}`;
      }
      
      return completeStory;
    } catch (error) {
      logger.error('Error generating story:', error);
      // If we can't connect to the API, provide a more helpful error message
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to the server. Please check if the server is running and try again.');
      }
      throw error;
    }
  },

  // Get setting suggestions
  getSettingSuggestions: async (count: number = 5, token: string | null = null): Promise<string[]> => {
    try {
      logger.log(`Fetching setting suggestions with count=${count}`);
      
      const headers = addAuthHeader({
        'Accept': 'application/json',
      }, token);
      
      const response = await fetchWithRetry(`${API_BASE_URL}/settings?count=${count}`, {
        method: 'GET',
        headers,
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get setting suggestions: ${response.status}`);
      }
      
      const data = await response.json() as SuggestionsResponse;
      return data.suggestions || [];
    } catch (error) {
      logger.error('Error getting setting suggestions:', error);
      // Return an empty array on error so the UI can still function
      return [];
    }
  },

  // Get character suggestions
  getCharacterSuggestions: async (count: number = 5, token: string | null = null): Promise<string[]> => {
    try {
      logger.log(`Fetching character suggestions with count=${count}`);
      
      const headers = addAuthHeader({
        'Accept': 'application/json',
      }, token);
      
      const response = await fetchWithRetry(`${API_BASE_URL}/characters?count=${count}`, {
        method: 'GET',
        headers,
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get character suggestions: ${response.status}`);
      }
      
      const data = await response.json() as SuggestionsResponse;
      return data.suggestions || [];
    } catch (error) {
      logger.error('Error getting character suggestions:', error);
      // Return an empty array on error so the UI can still function
      return [];
    }
  },

  // Get all stories for the current user
  getUserStories: async (token: string | null = null): Promise<StoryResponse[]> => {
    try {
      serviceCallCounts.getUserStories++;
      logger.log(`📊 StoryService.getUserStories called (total: ${serviceCallCounts.getUserStories})`);
      
      logger.log('⭐ Fetching user stories directly with Supabase client...');
      
      // Get user session
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to view your stories');
      }
      
      // Query stories directly from Supabase
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .eq('user_id', session.session.user.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw new Error(`Failed to fetch user stories: ${error.message}`);
      }
      
      if (!data || !Array.isArray(data)) {
        logger.error('Supabase returned non-array data:', data);
        return [];
      }
      
      // Transform database fields to client format
      return data.map(transformDatabaseStory);
    } catch (error) {
      logger.error('Error fetching user stories:', error);
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
      }
      throw error;
    }
  },
  
  // Get a single story by ID
  getStory: async (id: string, token: string | null = null): Promise<StoryResponse> => {
    try {
      logger.log(`⭐ Fetching story with ID ${id} directly with Supabase client...`);
      
      // Get user session
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to view your stories');
      }
      
      // Query the story directly from Supabase
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .eq('id', id)
        .eq('user_id', session.session.user.id)
        .single();
      
      if (error) {
        throw new Error(`Failed to fetch story: ${error.message}`);
      }
      
      if (!data) {
        throw new Error('Story not found');
      }
      
      // Transform database fields to client format
      return transformDatabaseStory(data);
    } catch (error) {
      logger.error('Error fetching story:', error);
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
      }
      throw error;
    }
  },
  
  // Get a shared story by share code
  getSharedStory: async (shareCode: string): Promise<StoryResponse> => {
    try {
      logger.log(`⭐ Fetching shared story with code ${shareCode} directly with Supabase client...`);
      
      // Query the shared story directly from Supabase
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .eq('share_code', shareCode)
        .eq('is_public', true)
        .single();
      
      if (error) {
        throw new Error(`Failed to fetch shared story: ${error.message}`);
      }
      
      if (!data) {
        throw new Error('Shared story not found or is not public');
      }
      
      // Transform database fields to client format
      return transformDatabaseStory(data);
    } catch (error) {
      logger.error('Error fetching shared story:', error);
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
      }
      throw error;
    }
  },
  
  // Get favorite stories
  getFavoriteStories: async (token: string | null = null): Promise<StoryResponse[]> => {
    try {
      serviceCallCounts.getFavoriteStories++;
      logger.log(`📊 StoryService.getFavoriteStories called (total: ${serviceCallCounts.getFavoriteStories})`);
      
      logger.log('⭐ Fetching favorite stories directly with Supabase client...');
      
      // Get user session
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to view your favorite stories');
      }
      
      // Query stories directly from Supabase
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .eq('user_id', session.session.user.id)
        .eq('is_favorite', true)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw new Error(`Failed to fetch favorite stories: ${error.message}`);
      }
      
      if (!data || !Array.isArray(data)) {
        logger.error('Supabase returned non-array data for favorites:', data);
        return [];
      }
      
      // Transform database fields to client format
      return data.map(transformDatabaseStory);
    } catch (error) {
      logger.error('Error fetching favorite stories:', error);
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
      }
      throw error;
    }
  },
  
  // Toggle a story's favorite status
  toggleFavorite: async (id: string, isFavorite: boolean, token: string | null = null): Promise<StoryResponse> => {
    try {
      logger.log(`⭐ Toggling favorite status for story ${id} to ${isFavorite} directly with Supabase client...`);
      
      // Get user session
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to update a story');
      }
      
      // Update the story directly in Supabase
      const { data, error } = await supabase
        .from('stories')
        .update({ is_favorite: isFavorite })
        .eq('id', id)
        .eq('user_id', session.session.user.id)
        .select()
        .single();
      
      if (error) {
        throw new Error(`Failed to update story favorite status: ${error.message}`);
      }
      
      if (!data) {
        throw new Error('Story not found or you do not have permission to modify it');
      }
      
      // Transform database fields to client format
      return transformDatabaseStory(data);
    } catch (error) {
      logger.error('Error updating story favorite status:', error);
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
      }
      throw error;
    }
  },
  
  // Update story visibility (make public/private)
  updateStoryVisibility: async (id: string, isPublic: boolean, token: string | null = null): Promise<StoryResponse> => {
    try {
      logger.log(`⭐ Updating visibility of story ${id} to ${isPublic ? 'public' : 'private'} directly with Supabase client...`);
      
      // Get user session
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to update a story');
      }
      
      // Update the story directly in Supabase
      const { data, error } = await supabase
        .from('stories')
        .update({ is_public: isPublic })
        .eq('id', id)
        .eq('user_id', session.session.user.id)
        .select()
        .single();
      
      if (error) {
        throw new Error(`Failed to update story visibility: ${error.message}`);
      }
      
      if (!data) {
        throw new Error('Story not found or you do not have permission to modify it');
      }
      
      // Transform database fields to client format
      return transformDatabaseStory(data);
    } catch (error) {
      logger.error('Error updating story visibility:', error);
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
      }
      throw error;
    }
  },
  
  // Delete a story
  deleteStory: async (id: string, token: string | null = null): Promise<void> => {
    try {
      logger.log(`⭐ Deleting story ${id} directly with Supabase client...`);
      
      // Get user session
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to delete a story');
      }
      
      // Delete the story directly from Supabase
      const { error } = await supabase
        .from('stories')
        .delete()
        .eq('id', id)
        .eq('user_id', session.session.user.id);
      
      if (error) {
        throw new Error(`Failed to delete story: ${error.message}`);
      }
    } catch (error) {
      logger.error('Error deleting story:', error);
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
      }
      throw error;
    }
  },
}; 